import { useCallback, useEffect, useRef } from 'react';

const FRAME_RATE = 30;
const HOLD_DELAY = 500;

enum ArrowKey {
  Left = 'ArrowLeft',
  Right = 'ArrowRight',
}

const useFrameStepNavigation = (
  videoRef: React.MutableRefObject<HTMLVideoElement | null>,
  enabled: boolean
) => {
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isPlaying = useRef<boolean>(!videoRef.current?.paused);
  const isKeyPressed = useRef<boolean>(false);

  const moveFrame = useCallback(
    (direction: number) => {
      if (!enabled) return;
      const videoElement = videoRef.current;

      if (!videoElement) return;

      const timeAdjustment = direction * (1 / FRAME_RATE);
      const newTime = Math.max(videoElement.currentTime + timeAdjustment, 0);

      videoElement.currentTime = Math.min(newTime, videoElement.duration);
    },
    [enabled, videoRef]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled || event.repeat) return;

      if (event.key === ArrowKey.Right || event.key === ArrowKey.Left) {
        isPlaying.current = !videoRef.current?.paused;
        videoRef.current?.pause();

        const direction = event.key === ArrowKey.Right ? 1 : -1;

        moveFrame(direction);

        timeoutRef.current = setTimeout(() => {
          isKeyPressed.current = true;
          intervalRef.current = setInterval(() => moveFrame(direction), 50);
        }, HOLD_DELAY);
      }
    },
    [enabled, moveFrame, videoRef]
  );

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      if (event.key === ArrowKey.Right || event.key === ArrowKey.Left) {
        if (isPlaying.current) {
          videoRef.current?.play();
        }

        clearTimeout(timeoutRef.current);
        clearInterval(intervalRef.current);
        isKeyPressed.current = false;
      }
    },
    [enabled, videoRef]
  );

  useEffect(() => {
    if (!enabled) return undefined;

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);

      clearTimeout(timeoutRef.current);
      clearInterval(intervalRef.current);
    };
  }, [enabled, handleKeyDown, handleKeyUp]);
};

export default useFrameStepNavigation;
